import uuid from 'uuid/v4';
import browserCookies from 'browser-cookies';
import pickBy from 'lodash/pickBy';
import get from 'lodash/get';
import { detect } from 'detect-browser';

import InsightsAPI from './api/insights';
import { cookieDomain, isNotEmpty, translateBrowserName } from './lib/helpers';
import * as EventProperties from './lib/events/event-properties';

const ON_NODE = (typeof window === 'undefined');
const EXPERIMENT_FORCE_PREFIX = 'experiment-force';
export const CLIENT_ID_COOKIE_NAME = 'PW_CLIENT_ID';
export const ANALYTICS_ID_COOKIE_NAME = 'PW_ANALYTICS_ID';

export default class Insights {
  static forceOption(experiment, query) {
    const key = `${EXPERIMENT_FORCE_PREFIX}-${experiment}`;
    if (query) {
      return query[key];
    }
    return null;
  }

  constructor({ baseURL, apiKey, cookies = {}, experiments = {}, batch = true } = {}) {
    let clientID;
    let analyticsID;

    if (ON_NODE && cookies) {
      clientID = cookies[CLIENT_ID_COOKIE_NAME];
      analyticsID = cookies[ANALYTICS_ID_COOKIE_NAME];
    } else {
      clientID = browserCookies.get(CLIENT_ID_COOKIE_NAME);
      analyticsID = browserCookies.get(ANALYTICS_ID_COOKIE_NAME);
    }

    if (!clientID || !analyticsID) {
      const year = 365 * 24 * 60 * 60 * 1000;
      const expiry = new Date(Date.now() + (5 * year));
      const domain = cookieDomain();

      if (!clientID) {
        clientID = uuid();
        if (!ON_NODE) {
          browserCookies.set(CLIENT_ID_COOKIE_NAME, clientID, { path: '/', expires: expiry, domain });
        }
      }
      if (!analyticsID) {
        analyticsID = uuid();
        if (!ON_NODE) {
          browserCookies.set(ANALYTICS_ID_COOKIE_NAME, analyticsID, { path: '/', expires: expiry, domain });
        }
      }
    }

    this.baseURL = baseURL;
    this.apiKey = apiKey;
    this.clientID = clientID;
    this.analyticsID = analyticsID;
    this.experiments = experiments;
    this.eventQueue = [];
    this.batch = batch;
    if (!ON_NODE) {
      window.setInterval(() => {
        this.flushEventQueue();
      }, 500);

      window.onbeforeunload = () => { this.flushEventQueue(); };
    }
  }

  associate({
    analyticsID = null,
    parkwhizID = null,
    emailSubscriberID = null,
    utmData = null,
    platform = 'desktop',
    userAgent = {},
    location = null,
    referringURL = null,
    affiliateID = null,
    legacyAffiliateID = null,
  }) {
    const browser = translateBrowserName(detect());
    this.analyticsID = analyticsID || this.analyticsID || uuid();
    this.parkwhizID = (parkwhizID && parkwhizID.toString()) || this.parkwhizID;
    this.emailSubscriberID = emailSubscriberID || this.emailSubscriberID;
    this.utmData = isNotEmpty(utmData) ? [utmData] : this.utmData;
    this.location = location || this.location;
    this.userAgent = userAgent || this.userAgent;
    this.browser = browser || this.browser;
    this.platform = platform || this.platform;
    this.referringURL = referringURL && referringURL !== '' ? referringURL : this.referringURL;
    this.pageUUID = uuid();

    if (affiliateID) {
      this.affiliateID = affiliateID;
    } else if (legacyAffiliateID) {
      this.affiliateID = `legacy-${affiliateID}`;
    }

    InsightsAPI.associate(this);
  }

  // eslint-disable-next-line
  trackAnalyticsEvent({
    batch = true,
    eventName = null,
    name = null,
    pageName = null,
    pageID = null,
    pageType = null,
    parentEventID = null,
    parentPageID = null,
    position = null,
    properties = null,
    type = null,
  }) {
    if (eventName === EventProperties.PAGEVIEW.eventName) {
      this.pageUUID = uuid();
    }
    const { clientID, analyticsID, pageUUID } = this;
    // The API accepts Unix nanosecond timestamps
    const timestamp = Date.now() * 1E6;
    const propertyList = properties && Object.keys(properties).map(k => (
      { name: k, value: `${properties[k]}` }
    )).filter(p => (p.value && p.value !== 'null'));
    const eventPayload = pickBy({
      timestamp,
      clientID,
      analyticsID,
      name,
      type,
      position: position && `${position}`,
      properties: propertyList,
      eventName,
      pageName,
      pageID,
      pageType,
      parentPageID,
      parentEventID,
      pageUUID,
    }, isNotEmpty);

    if (ON_NODE || !this.batch || !batch) {
      return InsightsAPI.trackAnalyticsEvent({
        baseURL: this.baseURL,
        apiKey: this.apiKey,
        events: [eventPayload],
      });
    }
    this.eventQueue.push(eventPayload);
  }

  participate({
    experiment,
    forceOption = null,
  }) {
    const {
      clientID,
      analyticsID,
      parkwhizID,
      platform,
      browser,
    } = this;

    return InsightsAPI.participate({
      baseURL: this.baseURL,
      apiKey: this.apiKey,
      experiment,
      forceOption,
      platform,
      browser,
      association: {
        clientID,
        analyticsID,
        parkwhizID,
      },
    });
  }

  flushEventQueue(retryEvents = null, retries = 3) {
    if (retryEvents || this.eventQueue.length > 0) {
      const events = retryEvents || this.eventQueue.slice();
      if (!this.retryEvents) {
        this.eventQueue = [];
      }

      InsightsAPI.trackAnalyticsEvent({
        baseURL: this.baseURL,
        apiKey: this.apiKey,
        events,
      }).catch((error) => {
        if (retries > 0 && get(error, 'response.status', 500) >= 500) {
          setTimeout(() => {
            this.flushEventQueue(events, retries - 1);
          }, 500);
        }
      });
    }
  }
}
